import { z } from "zod";
import { defineStepConfig } from "../../../registry/helpers";
import { entityOfType } from "../../../utils/system-entities";
import { SystemEntity } from "@erp/shared-types";
import { resolvedFilingThreadSchema } from "./resolve-collection-filing-target.step";

export const collectionEvidenceFilingSchema = z.object({
  threadId: z.uuid(),
  referenceCode: z.string(),
  /** False when the email was already filed on this thread (no-op). */
  filed: z.boolean(),
  attachmentCount: z.number().int().nonnegative(),
});

/**
 * File Collection Evidence Step
 *
 * Links the inbound email (and its substantive attachments) as evidence on
 * each resolved collection thread, records the filing on the thread activity
 * timeline, and posts the rep's accompanying comment when present.
 *
 * Idempotent per thread: re-runs and duplicate forwards are no-ops.
 */
export const fileCollectionEvidenceStep = defineStepConfig({
  type: "file_collection_evidence",
  name: "File Collection Evidence",
  description:
    "Files an inbound email and its attachments as evidence on collection threads",
  category: "Data Access",
  libraryModule: "AccountsReceivable",

  inputSchema: z.object({
    inboundEmail: entityOfType(SystemEntity.INBOUND_EMAIL).describe(
      "The email to file as evidence",
    ),
    actorUserId: z
      .uuid()
      .nullable()
      .optional()
      .describe(
        "The org user filing the evidence. Omitted/null for a system-driven auto-match filing.",
      ),
    threads: z
      .array(resolvedFilingThreadSchema)
      .describe("Collection threads to file the email onto"),
    comment: z
      .string()
      .nullable()
      .optional()
      .describe("Optional rep commentary to post as a thread comment"),
    filedBy: z
      .enum(["email", "auto_match"])
      .optional()
      .describe(
        "How the email reached the thread: rep COL-N forward (email) or content matcher (auto_match). Defaults to email.",
      ),
  }),

  outputSchema: z.object({
    filings: z
      .array(collectionEvidenceFilingSchema)
      .describe("Per-thread filing outcomes"),
    totalFiled: z
      .number()
      .int()
      .nonnegative()
      .describe("Number of threads the email was newly filed onto"),
  }),

  configSchema: z.object({}),

  emits: [] as const,
});
